import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Image as ImageIcon, LogOut, Trash2, Upload, Check, Circle, X } from "lucide-react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

function SubmissionsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contact");
      setRows(data);
    } catch {
      toast.error("Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markRead = async (id) => {
    await api.patch(`/contact/${id}/read`);
    setRows((r) => r.map((x) => (x.id === id ? { ...x, is_read: true } : x)));
  };

  const remove = async (id) => {
    await api.delete(`/contact/${id}`);
    setRows((r) => r.filter((x) => x.id !== id));
    setActive(null);
    toast.success("Submission deleted.");
  };

  const unread = rows.filter((r) => !r.is_read).length;

  return (
    <div data-testid="submissions-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-3xl text-charcoal">Contact Submissions</h2>
          <p className="font-sans text-sm text-charcoal/50 mt-1">
            {rows.length} total · {unread} unread
          </p>
        </div>
      </div>

      {loading ? (
        <p className="font-sans text-charcoal/50">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="border border-charcoal/15 py-20 text-center font-serif italic text-charcoal/50 text-xl" data-testid="submissions-empty">
          No submissions yet.
        </div>
      ) : (
        <div className="border border-charcoal/15 divide-y divide-charcoal/10">
          {rows.map((r) => (
            <button
              key={r.id}
              onClick={() => {
                setActive(r);
                if (!r.is_read) markRead(r.id);
              }}
              data-testid={`submission-row-${r.id}`}
              className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-charcoal/[0.03] transition-colors"
            >
              {r.is_read ? (
                <Check size={16} className="text-sage shrink-0" />
              ) : (
                <Circle size={10} className="text-terracotta fill-terracotta shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`font-sans text-sm truncate ${r.is_read ? "text-charcoal/70" : "text-charcoal font-semibold"}`}>
                  {r.name} <span className="text-charcoal/40">· {r.email}</span>
                </p>
                <p className="font-sans text-xs text-charcoal/50 truncate">
                  {r.project_type && `[${r.project_type}] `}{r.message}
                </p>
              </div>
              <span className="font-sans text-[11px] text-charcoal/40 shrink-0 hidden sm:block">
                {new Date(r.created_at).toLocaleDateString()}
              </span>
            </button>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setActive(null)}>
          <div
            className="bg-cream w-full max-w-lg p-8 relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="submission-detail"
          >
            <button onClick={() => setActive(null)} className="absolute top-5 right-5 text-charcoal/50 hover:text-charcoal" data-testid="submission-close">
              <X size={20} />
            </button>
            <p className="font-sans uppercase tracking-[0.25em] text-[10px] text-terracotta mb-4">Submission</p>
            <h3 className="font-serif text-3xl text-charcoal mb-1">{active.name}</h3>
            <p className="font-sans text-sm text-charcoal/60 mb-6">{new Date(active.created_at).toLocaleString()}</p>
            <dl className="space-y-3 font-sans text-sm">
              <div><dt className="text-charcoal/40 uppercase text-[10px] tracking-wider">Email</dt><dd className="text-charcoal">{active.email}</dd></div>
              {active.phone && <div><dt className="text-charcoal/40 uppercase text-[10px] tracking-wider">Phone</dt><dd className="text-charcoal">{active.phone}</dd></div>}
              {active.project_type && <div><dt className="text-charcoal/40 uppercase text-[10px] tracking-wider">Project</dt><dd className="text-charcoal">{active.project_type}</dd></div>}
              <div><dt className="text-charcoal/40 uppercase text-[10px] tracking-wider">Message</dt><dd className="text-charcoal leading-relaxed whitespace-pre-wrap">{active.message}</dd></div>
            </dl>
            <div className="flex gap-3 mt-8">
              <a href={`mailto:${active.email}`} className="flex-1 text-center bg-charcoal text-cream py-3 font-sans uppercase tracking-[0.2em] text-xs hover:bg-terracotta transition-colors">Reply by email</a>
              <button onClick={() => remove(active.id)} data-testid="submission-delete" className="px-4 border border-charcoal/20 text-charcoal/60 hover:text-terracotta hover:border-terracotta transition-colors">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gallery");
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !title) {
      toast.error("Add a photo and a title.");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("location", location);
    try {
      await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added.");
      setTitle("");
      setLocation("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/gallery/${id}`);
    setItems((x) => x.filter((i) => i.id !== id));
    toast.success("Photo removed.");
  };

  const inputCls = "w-full bg-transparent border-b border-charcoal/25 focus:border-terracotta outline-none py-2.5 font-sans text-sm text-charcoal placeholder:text-charcoal/40 transition-colors";

  return (
    <div data-testid="gallery-panel">
      <h2 className="font-serif text-3xl text-charcoal mb-6">Recent Jobs Gallery</h2>

      <form onSubmit={upload} className="border border-charcoal/15 p-6 mb-10 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="gallery-upload-form">
        <div className="space-y-6">
          <input data-testid="gallery-title" className={inputCls} placeholder="Title (e.g. Walnut Kitchen)" value={title} onChange={(e) => setTitle(e.target.value)} />
          <input data-testid="gallery-location" className={inputCls} placeholder="Location (e.g. Camas, WA)" value={location} onChange={(e) => setLocation(e.target.value)} />
        </div>
        <div className="space-y-4">
          <label className="flex items-center gap-3 border border-dashed border-charcoal/30 px-4 py-3 cursor-pointer hover:border-terracotta transition-colors">
            <Upload size={16} className="text-charcoal/60" />
            <span className="font-sans text-sm text-charcoal/60 truncate">{file ? file.name : "Choose a photo"}</span>
            <input ref={fileRef} data-testid="gallery-file" type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
          </label>
          <button type="submit" disabled={uploading} data-testid="gallery-upload-submit" className="w-full bg-charcoal text-cream py-3 font-sans uppercase tracking-[0.2em] text-xs hover:bg-terracotta transition-colors disabled:opacity-60">
            {uploading ? "Uploading…" : "Add to gallery"}
          </button>
        </div>
      </form>

      {loading ? (
        <p className="font-sans text-charcoal/50">Loading…</p>
      ) : items.length === 0 ? (
        <div className="border border-charcoal/15 py-16 text-center font-serif italic text-charcoal/50 text-xl" data-testid="gallery-empty-admin">
          No photos yet — upload your first job above.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((item) => (
            <div key={item.id} className="group relative border border-charcoal/10" data-testid={`gallery-admin-item-${item.id}`}>
              <div className="h-44 overflow-hidden">
                <img src={fileUrl(item.storage_path)} alt={item.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-3">
                <p className="font-serif text-charcoal truncate">{item.title}</p>
                {item.location && <p className="font-sans text-[11px] text-charcoal/50 truncate">{item.location}</p>}
              </div>
              <button
                onClick={() => remove(item.id)}
                data-testid={`gallery-delete-${item.id}`}
                className="absolute top-2 right-2 bg-charcoal/80 text-cream p-2 opacity-0 group-hover:opacity-100 hover:bg-terracotta transition-[opacity,background-color]"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("submissions");

  const doLogout = () => {
    logout();
    navigate("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col md:flex-row" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="md:w-72 bg-charcoal text-cream flex md:flex-col md:min-h-screen">
        <div className="p-6 md:p-8 flex-1 flex md:block items-center justify-between w-full">
          <Logo color="#f4e7d7" sub="#a4946c" />
          <nav className="hidden md:block mt-12 space-y-1">
            <button
              onClick={() => setTab("submissions")}
              data-testid="tab-submissions"
              className={`w-full flex items-center gap-3 px-4 py-3 font-sans text-sm uppercase tracking-[0.15em] transition-colors ${tab === "submissions" ? "bg-terracotta text-cream" : "text-cream/60 hover:text-cream"}`}
            >
              <Mail size={16} /> Submissions
            </button>
            <button
              onClick={() => setTab("gallery")}
              data-testid="tab-gallery"
              className={`w-full flex items-center gap-3 px-4 py-3 font-sans text-sm uppercase tracking-[0.15em] transition-colors ${tab === "gallery" ? "bg-terracotta text-cream" : "text-cream/60 hover:text-cream"}`}
            >
              <ImageIcon size={16} /> Gallery
            </button>
          </nav>
          {/* mobile tabs */}
          <div className="flex md:hidden gap-2">
            <button onClick={() => setTab("submissions")} className={`p-2 ${tab === "submissions" ? "text-terracotta" : "text-cream/60"}`}><Mail size={20} /></button>
            <button onClick={() => setTab("gallery")} className={`p-2 ${tab === "gallery" ? "text-terracotta" : "text-cream/60"}`}><ImageIcon size={20} /></button>
            <button onClick={doLogout} className="p-2 text-cream/60"><LogOut size={20} /></button>
          </div>
        </div>
        <div className="hidden md:block p-8 border-t border-cream/10">
          <p className="font-sans text-xs text-cream/40 mb-3 truncate">{user?.email}</p>
          <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-cream/60 hover:text-terracotta transition-colors">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 p-6 md:p-12 max-w-5xl">
        {tab === "submissions" ? <SubmissionsPanel /> : <GalleryPanel />}
      </main>
    </div>
  );
}
